import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const MAX_NOTIFICATIONS = 30;

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  body: string | null;
  read: boolean;
  created_at: string;
  reference_id?: string | null;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { isFounder } = useAuth();
  const [list, setList] = useState<NotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const prevUnreadRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);

  const fetchNotifications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setLoading(true);
      // Founders should not see business-level stock notifications.
      // We'll only sync low/dead stock notifications for non-founders.
      if (!isFounder) {
        await supabase.rpc("sync_low_stock_notifications");
      }
      const { data, error } = await supabase
        .from("notifications")
        .select("id, type, title, body, read, created_at, reference_id")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(MAX_NOTIFICATIONS);
      if (error) {
        setList([]);
        setUnreadCount(0);
        return;
      }
      let rows = (data ?? []) as NotificationRow[];
      if (isFounder) {
        // Founder dashboard should only show platform-wide / founder-relevant notifications.
        rows = rows.filter((r) => r.type === "broadcast");
      }
      const newUnread = rows.filter((r) => !r.read).length;
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      } else if (newUnread > prevUnreadRef.current && newUnread > 0) {
        const latest = rows.find((r) => !r.read);
        if (latest) toast.info(latest.title, { description: latest.body ?? undefined });
      }
      prevUnreadRef.current = newUnread;
      setList(rows);
      setUnreadCount(newUnread);
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const channel = supabase
      .channel("notifications")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications" },
        () => fetchNotifications()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isFounder]);

  const markRead = async (id: string) => {
    const { error } = await supabase.rpc("mark_notifications_read", {
      notification_ids: [id],
    });
    if (error) {
      toast.error("Could not mark as read. Please try again.");
      fetchNotifications();
      return;
    }
    setList((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    const { error } = await supabase.rpc("mark_notifications_read", {
      notification_ids: null,
    });
    if (error) {
      toast.error("Could not mark as read. Please try again.");
      fetchNotifications();
      return;
    }
    setList((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    fetchNotifications();
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    if (diffMs < 60000) return "Just now";
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground px-1">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 sm:w-[380px]" align="end" sideOffset={8}>
        <div className="border-b border-border px-4 py-3 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-primary hover:underline"
            >
              Mark all read
            </button>
          )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Low stock, dead stock, subscription reminders, team joins, AI insights ready, reorder alerts. Manage in Settings → Notifications.
          </p>
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {loading ? (
            <div className="py-8 flex justify-center">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 px-4 text-center">
              No notifications yet
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {list.map((n) => (
                <li
                  key={n.id}
                  className={cn(
                    "px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors",
                    !n.read && "bg-primary/5"
                  )}
                  onClick={() => {
                    if (!n.read) markRead(n.id);
                    if (n.reference_id && (n.type === "low_stock" || n.type === "dead_stock")) {
                      setOpen(false);
                      navigate(`/inventory?item=${n.reference_id}`);
                    } else if (n.type === "subscription_reminder") {
                      setOpen(false);
                      navigate("/settings/billing");
                    } else if (n.type === "daily_tip") {
                      setOpen(false);
                      navigate("/");
                    }
                  }}
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-sm">{n.title}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {formatDate(n.created_at)}
                    </span>
                  </div>
                  {n.body && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {n.body}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
